import React, { useState, useMemo } from 'react';
import { Button } from '../common';
import { useTopics } from '../../hooks/useTopics';
import { useSubtopics } from '../../hooks/useSubtopics';
import { Topic, Subtopic } from '../../types/topics';
import { useCompanyTags } from '../../hooks/useCompanyTags';
import { CompanyTag } from '../../types/companyTags';

interface PracticeSubtopicSelectProps {
  onSubtopicsSelected: (subtopicIds: number[] | undefined, companyTagIds: number[] | undefined) => void;
}

export const PracticeSubtopicSelect: React.FC<PracticeSubtopicSelectProps> = ({ onSubtopicsSelected }) => {
  const [selectedSubtopics, setSelectedSubtopics] = useState<Subtopic[]>([]);
  const [selectedCompanyTags, setSelectedCompanyTags] = useState<CompanyTag[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [activeTab, setActiveTab] = useState<'topics' | 'companies'>('topics');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: topics = [] } = useTopics();
  const { data: companyTags = [] } = useCompanyTags();
  const topicIds = useMemo(() => topics.map(t => t.id), [topics]);
  const { data: allSubtopics = [], isLoading: isLoadingSubtopics } = useSubtopics(topicIds);

  const handleSubtopicToggle = (subtopic: Subtopic) => {
    setSelectedSubtopics(prev => {
      const isSelected = prev.some(s => s.id === subtopic.id);
      if (isSelected) {
        return prev.filter(s => s.id !== subtopic.id);
      } else {
        return [...prev, subtopic];
      }
    });
  };

  const handleCompanyTagToggle = (companyTag: CompanyTag) => {
    setSelectedCompanyTags(prev => {
      const isSelected = prev.some(t => t.id === companyTag.id);
      if (isSelected) {
        return prev.filter(t => t.id !== companyTag.id);
      } else {
        return [...prev, companyTag];
      }
    });
  };

  const handleContinue = () => {
    const subtopicIds = selectedSubtopics.length > 0 ? selectedSubtopics.map(s => s.id) : undefined;
    const companyTagIds = selectedCompanyTags.length > 0 ? selectedCompanyTags.map(t => t.id) : undefined;
    onSubtopicsSelected(subtopicIds, companyTagIds);
  };

  // Group subtopics by topic
  const subtopicsByTopic = topics.map(topic => ({
    topic,
    subtopics: allSubtopics.filter((st: Subtopic) => st.topic_id === topic.id)
  }));

  if (isLoadingSubtopics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="relative">
          <div className="w-16 h-16 border-4 border-blue-500/20 rounded-full"></div>
          <div className="w-16 h-16 border-4 border-blue-500 rounded-full border-t-transparent animate-spin absolute top-0"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 bg-clip-text text-transparent">
          Select Topics & Companies to Practice
        </h1>
        <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Choose specific subtopics and/or companies to focus on, or leave unselected to practice all content.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50/50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl border border-red-100 dark:border-red-800 backdrop-blur-sm">
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex justify-center mb-6">
        <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100/50 dark:border-gray-700/50 p-1">
          <button
            onClick={() => setActiveTab('topics')}
            className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'topics'
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
            }`}
          >
            Topics
          </button>
          <button
            onClick={() => setActiveTab('companies')}
            className={`px-6 py-2 rounded-lg font-medium transition-all duration-200 ${
              activeTab === 'companies'
                ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
            }`}
          >
            Companies
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="md:col-span-1">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100/50 dark:border-gray-700/50 overflow-hidden h-[400px] flex flex-col">
            <div className="p-4 border-b border-gray-100/50 dark:border-gray-700/50 flex-shrink-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                {activeTab === 'topics' ? 'Topics' : 'Companies'}
              </h2>
            </div>
            <div className="overflow-y-auto flex-1">
              <div className="divide-y divide-gray-100/50 dark:divide-gray-700/50">
                {activeTab === 'topics' ? (
                  topics.map((topic) => (
                    <button
                      key={topic.id}
                      onClick={() => setSelectedTopic(topic)}
                      className={`w-full p-3 text-left transition-all duration-200 ${
                        selectedTopic?.id === topic.id
                          ? 'bg-blue-50/50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                          : 'hover:bg-gray-50/50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex items-center">
                        <span className="w-2 h-2 rounded-full bg-blue-500 mr-3"></span>
                        <span className="font-medium">{topic.name}</span>
                      </div>
                    </button>
                  ))
                ) : (
                  companyTags
                    .filter((tag: CompanyTag) => tag.is_active)
                    .map((companyTag: CompanyTag) => {
                      const isSelected = selectedCompanyTags.some(t => t.id === companyTag.id);
                      return (
                        <button
                          key={companyTag.id}
                          onClick={() => handleCompanyTagToggle(companyTag)}
                          className={`w-full p-3 text-left transition-all duration-200 ${
                            isSelected
                              ? 'bg-blue-50/50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                              : 'hover:bg-gray-50/50 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <span
                                className="w-3 h-3 rounded-full mr-3"
                                style={{ backgroundColor: companyTag.color || '#1890ff' }}
                              ></span>
                              <span className="font-medium">{companyTag.name}</span>
                            </div>
                            {isSelected && (
                              <span className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-md shadow-blue-500/20">
                                <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                </svg>
                              </span>
                            )}
                          </div>
                        </button>
                      );
                    })
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="md:col-span-3">
          {activeTab === 'topics' ? (
            selectedTopic ? (
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100/50 dark:border-gray-700/50 overflow-hidden h-[400px] flex flex-col">
                <div className="p-4 border-b border-gray-100/50 dark:border-gray-700/50 flex-shrink-0">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                    <span className="w-1.5 h-8 bg-gradient-to-b from-blue-500 to-indigo-500 rounded-full mr-3"></span>
                    {selectedTopic.name}
                  </h2>
                </div>
                <div className="overflow-y-auto flex-1">
                  <div className="p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {subtopicsByTopic
                        .find(({ topic }) => topic.id === selectedTopic.id)
                        ?.subtopics.map((subtopic: Subtopic) => {
                          const isSelected = selectedSubtopics.some(s => s.id === subtopic.id);
                          return (
                            <button
                              key={subtopic.id}
                              onClick={() => handleSubtopicToggle(subtopic)}
                              className={`group/subtopic p-3 rounded-lg border backdrop-blur-sm transition-all duration-200 ${
                                isSelected
                                  ? 'bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 border-blue-200/50 dark:border-blue-800/50 text-blue-700 dark:text-blue-300 shadow-md shadow-blue-500/10'
                                  : 'bg-white/50 dark:bg-gray-700/50 border-gray-200/50 dark:border-gray-600/50 text-gray-700 dark:text-gray-300 hover:bg-gray-50/80 dark:hover:bg-gray-700/80 hover:shadow-sm'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span className="font-medium">{subtopic.name}</span>
                                {isSelected && (
                                  <span className="w-5 h-5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-md shadow-blue-500/20">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                    </svg>
                                  </span>
                                )}
                              </div>
                            </button>
                          );
                        })}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[400px] bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100/50 dark:border-gray-700/50 p-8">
                <p className="text-lg text-gray-500 dark:text-gray-400">Select a topic to view its subtopics</p>
              </div>
            )
          ) : (
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100/50 dark:border-gray-700/50 overflow-hidden h-[400px] flex flex-col">
              <div className="p-4 border-b border-gray-100/50 dark:border-gray-700/50 flex-shrink-0">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                  <span className="w-1.5 h-8 bg-gradient-to-b from-green-500 to-emerald-500 rounded-full mr-3"></span>
                  Company-Specific Content
                </h2>
              </div>
              <div className="overflow-y-auto flex-1">
                <div className="p-4">
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Practice with content specifically tagged for these companies. This helps you prepare for company-specific interviews and assessments.
                  </p>
                  {selectedCompanyTags.length > 0 && (
                    <div className="mb-4">
                      <h3 className="font-medium text-gray-900 dark:text-white mb-2">Selected Companies:</h3>
                      <div className="flex flex-wrap gap-2">
                        {selectedCompanyTags.map((tag: CompanyTag) => (
                          <span
                            key={tag.id}
                            className="px-3 py-1 rounded-full text-sm font-medium text-white"
                            style={{ backgroundColor: tag.color || '#1890ff' }}
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 flex justify-center">
        <Button
          onClick={handleContinue}
          variant="primary"
          className="w-full max-w-md h-12 text-lg font-medium rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500"
          disabled={isLoading}
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              <span>Loading...</span>
            </div>
          ) : (
            <span className="flex items-center justify-center">
              Continue
              <svg className="w-5 h-5 ml-2 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
          )}
        </Button>
      </div>
    </div>
  );
};